import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { SearchField } from "@/components/SearchField/SearchField";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";
import { Order } from "@/types/types";

import { useGetLeanListsQuery } from "../../../redux/list/list.api";
import { List } from "../../List/schema/list";

interface SelectListPopoverProps {
	onSelectList: (list: { id: number; name: string }) => void;
	onCreateNew: (name: string) => void;
	isLoading?: boolean;
	excludeListId?: number;
}

export const SelectListPopover: FunctionComponent<SelectListPopoverProps> = ({
	onSelectList,
	onCreateNew,
	isLoading,
	excludeListId,
}) => {
	const { t } = useTranslation();

	const [searchValue, setSearchValue] = useState("");
	const [query, setQuery] = useState("");
	const [showCreateInput, setShowCreateInput] = useState(false);
	const [newListName, setNewListName] = useState("");

	const searchTimer = useRef<NodeJS.Timeout | null>(null);
	const createInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}

		searchTimer.current = setTimeout(() => {
			setQuery(searchValue);
		}, 400);

		return () => {
			if (searchTimer.current) {
				clearTimeout(searchTimer.current);
			}
		};
	}, [searchValue]);

	useEffect(() => {
		if (showCreateInput && createInputRef.current) {
			createInputRef.current.focus();
		}
	}, [showCreateInput]);

	// Archived lists are hidden from the Lead List Overview, so they are no valid destination —
	// leads added to one would land somewhere the user cannot see afterwards (PREC-3754).
	const { data: listsResponse, isFetching } = useGetLeanListsQuery({
		page: 1,
		limit: 50,
		searchValue: query,
		archived: false,
		sorting: { sortBy: "updatedAt", sortOrder: Order.DESC },
	});

	const lists = (listsResponse?.data || []).filter(
		(list: List) => excludeListId === undefined || list.id !== excludeListId
	);

	const handleCreateNew = () => {
		if (newListName.trim().length >= 4) {
			onCreateNew(newListName.trim());
			setNewListName("");
			setShowCreateInput(false);
		}
	};

	return (
		<div className="w-[280px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[2000]">
			<div className="p-2">
				<SearchField
					handleChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
					name="list-search"
					placeholder={t("basics.search", { something: "lists" })}
					value={searchValue}
				/>
			</div>

			<div className="max-h-[240px] overflow-y-auto bb-scrollbar-darker">
				{isFetching ? (
					<div className="flex items-center justify-center py-4">
						<Spinner color="#6B7280" />
					</div>
				) : lists.length > 0 ? (
					lists.map((list: List) => (
						<button
							key={list.id}
							className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer duration-150"
							disabled={isLoading}
							onClick={() => onSelectList({ id: list.id, name: list.name })}
						>
							{list.name}
						</button>
					))
				) : (
					<p className="px-3 py-2 text-sm italic text-gray-500">{t("basics.noResults")}</p>
				)}
			</div>

			<div className="border-t border-gray-200">
				{showCreateInput ? (
					<div className="p-2 flex gap-2">
						<input
							ref={createInputRef}
							className="input-styles flex-1 text-sm h-[32px] px-2"
							placeholder={t("contacts.newListName")}
							type="text"
							value={newListName}
							onChange={(e) => setNewListName(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									handleCreateNew();
								} else if (e.key === "Escape") {
									setShowCreateInput(false);
									setNewListName("");
								}
							}}
						/>
						<button
							className={classNames(
								"text-sm font-semibold px-3 py-1 rounded-lg duration-150",
								newListName.trim().length >= 4
									? "bg-blue-action text-white cursor-pointer hover:shadow-md"
									: "bg-gray-200 text-gray-400 cursor-not-allowed"
							)}
							disabled={newListName.trim().length < 4 || isLoading}
							onClick={handleCreateNew}
						>
							{t("basics.createSomething", { something: "list" })}
						</button>
					</div>
				) : (
					<button
						className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-blue-action hover:bg-gray-50 cursor-pointer duration-150 font-medium"
						onClick={() => setShowCreateInput(true)}
					>
						<Icon className="w-4" icon={faPlus} />
						{t("contacts.createNewList")}
					</button>
				)}
			</div>
		</div>
	);
};
